import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { getLanguageValue } from 'locales/i18n';
import { PAGE_TYPE } from 'enums';
import * as InAppHelper from "helpers/InApp.helper";

interface KoachLayoutMenuProps {
    needNoMenu?: boolean;
}

const KoachLayoutMenu = (props: KoachLayoutMenuProps) => {
    const { needNoMenu } = props;
    const location = useLocation();
    const pathname = location.pathname;
    const [menuPb, setMenuPb] = useState('pb-4');

    const getIconSrc = (path: string, icon: string) => {
        return path === '/koach'
            ? pathname === path ? `./imgs/icons/${icon}-active.svg` : `./imgs/icons/${icon}.svg`
            : pathname.startsWith(path) ? `./imgs/icons/${icon}-active.svg` : `./imgs/icons/${icon}.svg`;
    };

    // 서브 메뉴가 있는 경로인지 확인
    const hasSubMenu = pathname.startsWith('/koach/coaching') ||
        pathname.startsWith('/koach/sales') ||
        pathname.startsWith('/koach/profile');

    // 메인 메뉴 표시 조건: /koach이거나 서브 메뉴가 없는 경로
    const showMainMenu = pathname === '/koach' || (pathname.startsWith('/koach/') && !hasSubMenu);

    const updateMenuPb = async () => {
        const isIos = await InAppHelper.getIsIos();
        if (!isIos) {
            setMenuPb("pb-8");
        }
    }

    useEffect(() => {
        updateMenuPb();
    }, [pathname]);

    return (
        <div className={`fixed bottom-0 left-0 right-0 flex justify-center ${menuPb} transition-all duration-500
            ${needNoMenu
                ? 'opacity-0 pointer-events-none translate-y-8'
                : 'opacity-100 pointer-events-auto translate-y-0'
            }`}
            style={{
                transitionProperty: 'opacity, transform',
            }}>
            <div className={`bg-white/80 backdrop-blur-md rounded-2xl shadow-sm px-1 py-1 transition-all duration-300 ${showMainMenu ? 'opacity-100 translate-y-0 visible' : 'opacity-0 translate-y-full invisible'
                }`}>
                <div className="flex gap-2 sm:gap-4 md:gap-6">
                    {/* 홈 */}
                    <Link to="/koach" className="group flex flex-col items-center transition-all hover:scale-110">
                        <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                            <img src={getIconSrc('/koach', 'icon-home')} alt={getLanguageValue(PAGE_TYPE.KOACH, "mainMenu.txtHome")} className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1" />
                            <p className={`text-[10px] sm:text-xs font-medium ${pathname === '/koach' ? 'text-[#CF3688]' : 'text-gray-700'} text-center`}>
                                {getLanguageValue(PAGE_TYPE.KOACH, "mainMenu.txtHome")}
                            </p>
                        </div>
                    </Link>

                    {/* 코칭 */}
                    <Link to="/koach/coaching" className="group flex flex-col items-center transition-all hover:scale-110">
                        <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                            <img src={getIconSrc('/koach/coaching', 'icon-coaching')} alt={getLanguageValue(PAGE_TYPE.KOACH, "mainMenu.txtCoaching")} className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1" />
                            <p className={`text-[10px] sm:text-xs font-medium ${pathname.startsWith('/koach/coaching') ? 'text-[#CF3688]' : 'text-gray-700'} text-center`}>
                                {getLanguageValue(PAGE_TYPE.KOACH, "mainMenu.txtCoaching")}
                            </p>
                        </div>
                    </Link>

                    {/* 상품 */}
                    <Link to="/koach/sales" className="group flex flex-col items-center transition-all hover:scale-110">
                        <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                            <img src={getIconSrc('/koach/sales', 'icon-contents')} alt="상품" className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1" />
                            <p className={`text-[10px] sm:text-xs font-medium ${pathname.startsWith('/koach/sales') ? 'text-[#CF3688]' : 'text-gray-700'} text-center`}>
                                상품
                            </p>
                        </div>
                    </Link>

                    {/* 프로필 */}
                    <Link to="/koach/profile" className="group flex flex-col items-center transition-all hover:scale-110">
                        <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                            <img src={getIconSrc('/koach/profile', 'icon-profile')} alt="프로필" className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1" />
                            <p className={`text-[10px] sm:text-xs font-medium ${pathname.startsWith('/koach/profile') ? 'text-[#CF3688]' : 'text-gray-700'} text-center`}>
                                프로필
                            </p>
                        </div>
                    </Link>

                </div>
            </div>

            {/* 코칭 서브 메뉴 */}
            <div className={`absolute ${menuPb} bottom-0 left-0 right-0 flex justify-center transition-all duration-300 ${pathname.startsWith('/koach/coaching') ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-full invisible'}`}>
                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-sm px-1 py-1">
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-4 items-center">
                        <Link to="/koach" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <img src="./imgs/icons/icon-arrow-left-1.svg" alt="이전" className="w-4 h-4 sm:w-5 sm:h-5 mx-auto" />
                            </div>
                        </Link>
                        <div className="hidden sm:block h-5 w-[1px] bg-gray-200"></div>
                        <Link to="/koach/coaching/diary" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/koach/coaching/diary' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    성장 다이어리
                                </p>
                            </div>
                        </Link>
                        <Link to="/koach/coaching/contents" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/koach/coaching/contents' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    콘텐츠
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>

            {/* 상품 서브 메뉴 */}
            <div className={`absolute ${menuPb} bottom-0 left-0 right-0 flex justify-center transition-all duration-300 
                        ${pathname.startsWith('/koach/sales') ? 'opacity-100 translate-y-0 visible' : 'opacity-0 translate-y-full invisible'}`}>
                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-sm px-1 py-1">
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-4 items-center">
                        <Link to="/koach" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <img src="./imgs/icons/icon-arrow-left-1.svg" alt="Previous" className="w-4 h-4 sm:w-5 sm:h-5 mx-auto" />
                            </div>
                        </Link>
                        <div className="hidden sm:block h-5 w-[1px] bg-gray-200"></div>
                        <Link to="/koach/sales/product" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/koach/sales/product' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    상품 관리
                                </p>
                            </div>
                        </Link>
                        <Link to="/koach/sales/status" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/koach/sales/status' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    판매 현황
                                </p>
                            </div>
                        </Link>
                        <Link to="/koach/sales/settlement" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/koach/sales/settlement' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    정산
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>

            {/* 프로필 서브 메뉴 */}
            <div className={`absolute ${menuPb} bottom-0 left-0 right-0 flex justify-center transition-all duration-300 
                        ${pathname.startsWith('/koach/profile') ? 'opacity-100 translate-y-0 visible' : 'opacity-0 translate-y-full invisible'}`}>
                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-sm px-1 py-1">
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-4 items-center">
                        <Link to="/koach" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <img src="./imgs/icons/icon-arrow-left-1.svg" alt="Previous" className="w-4 h-4 sm:w-5 sm:h-5 mx-auto" />
                            </div>
                        </Link>
                        <div className="hidden sm:block h-5 w-[1px] bg-gray-200"></div>
                        <Link to="/koach/profile/basic" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/koach/profile/basic' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    기본 정보
                                </p>
                            </div>
                        </Link>
                        <Link to="/koach/profile/review" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/koach/profile/review' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    평가 & 신뢰
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>

            {/* 캐스팅 메뉴 */}
            <div className={`absolute ${menuPb} bottom-0 left-0 right-0 flex justify-center transition-all duration-300 ${pathname.startsWith('/student/casting') ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-full invisible'}`}>
                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-sm px-1 py-1">
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-4 items-center">
                        <Link to="/student" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <img src="./imgs/icons/icon-arrow-left-1.svg" alt="Previous" className="w-4 h-4 sm:w-5 sm:h-5 mx-auto" />
                            </div>
                        </Link>
                        <div className="hidden sm:block h-5 w-[1px] bg-gray-200"></div>
                        <Link to="/student/casting/" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/student/casting' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "castingMenu.txtSummary")}
                                </p>
                            </div>
                        </Link>
                        <Link to="/student/casting/message" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/student/casting/message' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "castingMenu.txtMessage")}
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>

            {/* 커뮤니티 메뉴 */}
            {/* <div className={`absolute pb-2 bottom-0 left-0 right-0 flex justify-center transition-all duration-300 ${pathname.startsWith('/student/hub') ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-full invisible'}`}>
                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-sm px-1 py-1">
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-4 items-center">
                        <Link to="/student" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <img src="./imgs/icons/icon-arrow-left-1.svg" alt="Previous" className="w-4 h-4 sm:w-5 sm:h-5 mx-auto" />
                            </div>
                        </Link>
                        <div className="hidden sm:block h-5 w-[1px] bg-gray-200"></div>
                        <Link to="/student/hub/" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/student/hub' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtSummary")}
                                </p>
                            </div>
                        </Link>
                        <Link to="/student/hub/community" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/student/hub/community' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtCommunity")}
                                </p>
                            </div>
                        </Link>
                        <Link to="/student/hub/follow" className="group flex flex-col items-center transition-all hover:scale-110">
                            <div className="p-2 sm:p-3 rounded-xl group-hover:bg-gray-50">
                                <p className={`text-xs sm:text-sm font-medium ${pathname === '/student/hub/follow' ? 'text-[#CF3688]' : 'text-gray-700'} text-center whitespace-nowrap`}>
                                    {getLanguageValue(PAGE_TYPE.STUDENT, "hubMenu.txtFollow")}
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>
            </div> */}

            {/* 프로필 서브 메뉴 */}
            {/* <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-sm px-1 py-1">
                <div className="flex gap-6">
                    <Link to="/student/public-profile" className="group flex flex-col items-center transition-all hover:scale-110">
                        <div className="p-3 rounded-xl">
                            <img src={getIconSrc('/student/public-profile', 'icon-profile')} alt="프로필 수정" />
                        </div>
                    </Link>
                </div>
            </div> */}
        </div>
    );
};

export default KoachLayoutMenu;